// libraries
import cn from 'classnames'
import Button from 'ext-ui/components/Button/Button'
import Subnav from 'ext-ui/components/Subnav/Subnav'
import moment from 'moment'
import queryString from 'query-string'
import React, { createContext, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { selectReceiptsAutoMatchEnabledFromState } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import BackgroundPage from 'src/components/BackgroundPage/BackgroundPage'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import NotificationDetails from 'src/components/NotificationDetails/NotificationDetails'
import NotificationsList from 'src/components/NotificationsList/NotificationsList'
import TaskDetails from 'src/components/TaskDetails/TaskDetails'
import TasksComplete from 'src/components/TasksComplete/TasksComplete'
import TasksList from 'src/components/TasksList/TasksList'
import { useTranslate } from 'src/localization/Trns'
import { activityUrl } from 'src/navigation/urls'
import {
  TASK_COUNT_ENABLED,
  useFetchNotificationsCount,
  useFetchTasksCount,
  usePostReadAllNotifications,
} from 'src/queries/activity.queries'
import { createSetIsUploadReceiptsAutoMatchPopupOpen } from 'src/redux/actions/app.actions'
import { LocationState } from 'src/redux/createRootReducer'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'

import style from './Activity.style'

interface ActivityContextType {
  tasksComplete: boolean
  setTasksComplete: (empty: boolean) => void
}

export const ActivityContext = createContext<ActivityContextType>({
  tasksComplete: false,
  setTasksComplete: () => {},
})

const Activity: React.FC = () => {
  const appName = useSelector(selectAppName)

  const [tab, setTab] = React.useState('tasks')
  const [taskId, setTaskId] = React.useState<string | undefined>(undefined)
  const [tasksComplete, setTasksComplete] = useState(false)

  const translate = useTranslate()
  const history = useHistory<LocationState>()
  const location = useSelector(selectLocationFromState)

  const receiptsAutoMatchEnabled = useSelector(
    selectReceiptsAutoMatchEnabledFromState,
  )

  const dispatch = useDispatch()

  useEffect(() => {
    if (location.pathname !== activityUrl()) {
      return
    }

    const params = queryString.parse(location.search)
    const selectedTab = params.tab as 'tasks' | 'notifications' | undefined

    setTab(selectedTab || 'tasks')

    if (selectedTab === undefined) {
      history.replace({
        pathname: activityUrl(),
        search: `?tab=tasks`,
      })
    }

    if (selectedTab === 'tasks') {
      queryString.parse(location.search)
      const params = queryString.parse(location.search)
      const taskId = params.id ? (params.id as string) : undefined

      setTaskId(taskId)
    }
  }, [location.search])

  const {
    mutate: postReadAllNotifications,
    isPending: postReadAllNotificationsLoading,
  } = usePostReadAllNotifications()
  const { data: notificationsCount, isLoading: isLoadingNotifications } =
    useFetchNotificationsCount()
  const { data: tasksCount, isLoading: isLoadingTasks } = useFetchTasksCount()

  const renderTaskLoading = tab === 'tasks' && !tasksComplete && !taskId
  const renderTaskDetail = tab === 'tasks' && !tasksComplete && taskId
  const renderTasksComplete = tab === 'tasks' && tasksComplete
  const renderNotificationDetail = tab === 'notifications'

  const handleMarkAllAsRead = (): void => {
    postReadAllNotifications({
      body: {
        seenAt: moment().add(1, 'ms').format('YYYY-MM-DDTHH:mm:ss.SSSZZ'),
      },
    })
  }

  const handleAutoMatchReceipts = (): void => {
    if (receiptsAutoMatchEnabled) {
      dispatch(createSetIsUploadReceiptsAutoMatchPopupOpen(true))
    }
  }

  return (
    <>
      <Helmet>
        <title>{`Activity | ${appName}`}</title>
        <meta name="description" content={`Activity page of ${appName}`} />
      </Helmet>
      <ActivityContext.Provider value={{ tasksComplete, setTasksComplete }}>
        <BackgroundPage css={style} className={cn('Activity', 'flex-row')}>
          <div css={style} className="Activity-list">
            <div className="Activity-list-header">
              <div className="Activity-list-title">{translate('Activity')}</div>
              {tab === 'tasks' && receiptsAutoMatchEnabled && (
                <Button variant="text" onClick={handleAutoMatchReceipts}>
                  <span>{translate('Auto-Match Receipts')}</span>
                </Button>
              )}
              {tab === 'notifications' && (
                <Button
                  variant="text"
                  onClick={handleMarkAllAsRead}
                  disabled={notificationsCount === 0}
                  loading={postReadAllNotificationsLoading}
                >
                  <span>{translate('Mark All As Read')}</span>
                </Button>
              )}
            </div>
            <Subnav
              tabs={[
                {
                  children: <>{translate('My Tasks')}</>,
                  onClick: (): void => {
                    history.push({
                      pathname: activityUrl(),
                      search: '?tab=tasks',
                    })
                  },
                  active: tab === 'tasks',
                  countLoading: TASK_COUNT_ENABLED && isLoadingTasks,
                  count: TASK_COUNT_ENABLED ? tasksCount : undefined,
                },
                {
                  children: <>{translate('Notifications')}</>,
                  onClick: (): void => {
                    history.push({
                      pathname: activityUrl(),
                      search: '?tab=notifications',
                    })
                  },
                  active: tab === 'notifications',
                  countLoading: isLoadingNotifications,
                  count: notificationsCount,
                },
              ]}
            />
            {tab === 'tasks' && <TasksList />}
            {tab === 'notifications' && <NotificationsList />}
          </div>
          <div className={cn('Activity-details')}>
            {renderTaskLoading && <LoadingBackgroundPage />}
            {renderTaskDetail && <TaskDetails key={taskId} id={taskId} />}
            {renderTasksComplete && <TasksComplete />}
            {renderNotificationDetail && <NotificationDetails />}
          </div>
        </BackgroundPage>
      </ActivityContext.Provider>
    </>
  )
}

export default Activity
